import Dropdown from "../base/Dropdown";
import classNames from "classnames";
import isEqual from "lodash/isEqual";
import {
  CSSProperties,
  ComponentProps,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  memo,
  useCallback,
  useEffect,
  useState,
} from "react";
import { EllipsisVerticalIcon, NotePads30x33Icon } from "~@/icons";

export enum MenuSize {
  Small = "sm",
  Medium = "md",
  Large = "lg",
  ExtraLarge = "xlg",
}

type ActionDropdownProps = (
  | {
      hasExternalState?: false;
      isOpen?: undefined;
      setIsOpen?: undefined;
    }
  | {
      hasExternalState: true;
      isOpen: boolean;
      setIsOpen: Dispatch<SetStateAction<boolean>>;
    }
) & {
  content: (props: { closeDropdown: () => void }) => ReactNode;
  triggerElement?: (props: {
    isDropdownOpen: boolean;
    toggleDropdown: () => void;
  }) => ReactNode;
  menuSize?: MenuSize;
  contentStyles?: CSSProperties;
  disabled?: boolean;
  emptyText?: string;
  className?: string;
} & Omit<
    ComponentProps<typeof Dropdown>,
    "isDropdownOpen" | "onRequestClose" | "dropdownClassName" | "children"
  >;

const ActionDropdown: FC<ActionDropdownProps> = ({
  hasExternalState,
  isOpen,
  setIsOpen,
  content,
  triggerElement,
  menuSize = MenuSize.Large,
  contentStyles,
  disabled = false,
  emptyText,
  className,
  ...props
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(
    hasExternalState ? isOpen : false
  );

  const closeDropdown = useCallback(() => {
    if (isDropdownOpen) {
      setIsDropdownOpen(false);
    }
  }, [isDropdownOpen]);

  const toggleDropdown = useCallback(() => {
    setIsDropdownOpen(!isDropdownOpen);
  }, [isDropdownOpen]);

  const updateExternalState = useCallback(() => {
    if (!hasExternalState) return;

    setIsOpen(isDropdownOpen);
  }, [hasExternalState, isDropdownOpen, setIsOpen]);

  useEffect(() => {
    updateExternalState();
  }, [updateExternalState]);
  return (
    <Dropdown
      isDropdownOpen={isDropdownOpen}
      onRequestClose={closeDropdown}
      dropdownClassName={classNames("action-dropdown", className)}
      {...props}
    >
      {({ menuReference, styles, attributes }) => {
        return (
          <>
            <div className="dropdown__trigger">
              {triggerElement instanceof Object ? (
                triggerElement({ isDropdownOpen, toggleDropdown })
              ) : (
                <button
                  disabled={disabled}
                  className={classNames("action-dropdown__button", {
                    "action-dropdown__button--is-active": isDropdownOpen,
                  })}
                  type="button"
                  onClick={toggleDropdown}
                >
                  <span
                    className={classNames("icon icon--is-14x14", {
                      "icon--is-gray-4": disabled,
                    })}
                  >
                    <EllipsisVerticalIcon />
                  </span>
                </button>
              )}
            </div>
            {isDropdownOpen && (
              <div
                className={classNames(
                  "dropdown__menu action-dropdown__menu",
                  `action-dropdown__menu--is-${menuSize}`
                )}
                role="menu"
                ref={menuReference}
                style={styles.popper}
                {...attributes.popper}
              >
                <div
                  style={contentStyles}
                  className="dropdown__content action-dropdown__content"
                >
                  {emptyText ? (
                    <div className="info-placeholder__body">
                      <div className="block block--has-mb-8">
                        <div className="icon icon--has-default-size">
                          <NotePads30x33Icon />
                        </div>
                      </div>
                      <div className="block block--has-mb-4">
                        <h2 className="info-placeholder__title info-placeholder__title--is-centered">
                          {emptyText}
                        </h2>
                      </div>
                    </div>
                  ) : (
                    content({ closeDropdown })
                  )}
                </div>
              </div>
            )}
          </>
        );
      }}
    </Dropdown>
  );
};

export default memo(ActionDropdown, isEqual);
